// Transform panel (DRAW_PLAN §1 "Arrange"): X / Y / W / H of the selection's bounds at the chosen reference
// point, rotation and shear, in document units. Typing a value applies one undoable transform. For several
// objects, rotation and shear are applied as a change from 0.
import { useState } from 'react';
import { useT } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { currentList } from '../../render/instance';
import { selectionBounds } from '../../tools/selection';
import { about, decompose, mul, rotate, scale, shear, translate } from '../../geometry/mat';
import { transformNodes } from '../../commands/commands';
import { findNode } from '../../model/tree';
import { fromPt, toPt } from '../../model/units';
import type { Mat2D, Rect } from '../../model/types';

const REF: [number, number][] = [
  [0, 0],
  [0.5, 0],
  [1, 0],
  [0, 0.5],
  [0.5, 0.5],
  [1, 0.5],
  [0, 1],
  [0.5, 1],
  [1, 1],
];
const refPoint = (r: Rect, k: number): [number, number] => [r.x + REF[k]![0] * r.w, r.y + REF[k]![1] * r.h];

function applyToSelection(delta: Mat2D) {
  const st = useDesign.getState();
  const sel = useDesignUi.getState().selection;
  if (!st.doc) return;
  const next = new Map<string, Mat2D>();
  for (const id of sel) {
    const f = findNode(st.doc, id);
    if (f) next.set(id, mul(delta, f.node.transform));
  }
  st.run(transformNodes(next));
}

export function TransformPanel() {
  const t = useT();
  useDesign((st) => st.version); // re-render on edits
  const doc = useDesign((st) => st.doc)!;
  const sel = useDesignUi((st) => st.selection);
  const [ref, setRef] = useState(4);
  const box = selectionBounds(currentList(), sel);
  if (!box) return <p style={{ opacity: 0.7 }}>{t('dockEmpty')}</p>;
  const u = doc.units,
    dpi = doc.rasterDpi;
  const single = sel.length === 1 ? findNode(doc, sel[0]!)?.node : undefined;
  const dec = single ? decompose(single.transform) : null;
  const [rx, ry] = refPoint(box, ref);
  const num = (v: number) => Number(v.toFixed(3));
  const field = (label: string, testid: string, value: number, commit: (v: number) => void) => (
    <label key={testid} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 52 }}>{label}</span>
      <input
        type="number"
        step="any"
        data-testid={testid}
        defaultValue={num(value)}
        key={`${testid}:${num(value)}`}
        style={{ width: 90 }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        onBlur={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v) && Math.abs(v - num(value)) > 1e-9) commit(v);
        }}
      />
    </label>
  );
  return (
    <div data-testid="transform-panel" style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div
          role="radiogroup"
          aria-label={t('refPoint')}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 14px)', gap: 2 }}
        >
          {REF.map((_, k) => (
            <button
              key={k}
              role="radio"
              aria-checked={ref === k}
              aria-label={`${t('refPoint')} ${k + 1}`}
              onClick={() => setRef(k)}
              style={{
                width: 14,
                height: 14,
                padding: 0,
                background: ref === k ? 'var(--accent)' : undefined,
              }}
            />
          ))}
        </div>
        <span style={{ opacity: 0.7 }}>{t('refPoint')}</span>
      </div>
      {field('X', 'tf-x', fromPt(rx, u, dpi), (v) => applyToSelection(translate(toPt(v, u, dpi) - rx, 0)))}
      {field('Y', 'tf-y', fromPt(ry, u, dpi), (v) => applyToSelection(translate(0, toPt(v, u, dpi) - ry)))}
      {field(
        t('width'),
        'tf-w',
        fromPt(box.w, u, dpi),
        (v) => box.w > 0 && applyToSelection(about(scale(toPt(v, u, dpi) / box.w, 1), rx, ry)),
      )}
      {field(
        t('height'),
        'tf-h',
        fromPt(box.h, u, dpi),
        (v) => box.h > 0 && applyToSelection(about(scale(1, toPt(v, u, dpi) / box.h), rx, ry)),
      )}
      {field(t('rotation'), 'tf-rot', dec ? (-dec.rotation * 180) / Math.PI : 0, (v) => {
        const cur = dec ? (-dec.rotation * 180) / Math.PI : 0;
        applyToSelection(about(rotate((-(v - cur) * Math.PI) / 180), rx, ry));
      })}
      {field(t('shear'), 'tf-shear', dec ? (dec.shear * 180) / Math.PI : 0, (v) => {
        const cur = dec ? (dec.shear * 180) / Math.PI : 0;
        applyToSelection(about(shear(((v - cur) * Math.PI) / 180), rx, ry));
      })}
      <div style={{ opacity: 0.6 }}>{t('unitsAre', { unit: u })}</div>
    </div>
  );
}
